import React, { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { FadeUp } from "../components/Motion";
import { ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";

// Section definitions for the Our Work page.
// Residential and Commercial render as photo cards; the rest as text rows.
const SECTIONS = [
  {
    key: "residential",
    label: "Residential",
    items: [
      {
        slug: "north-tampa-custom-home",
        title: "North Tampa Custom Home",
        meta: "North Tampa",
        img: "/images/projects/bcpeabody-north-tampa-custom-home-primary-bath-07.jpg",
        alt: "Primary bathroom with freestanding tub, backlit onyx column and marble walls, North Tampa custom home",
        pos: "object-[center_60%]",
      },
      {
        slug: "waverly-park-kitchen",
        title: "Waverly Park Kitchen & Whole-Home Flooring",
        meta: "South Tampa",
        img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/gfxrb2yy_After%201.jpeg",
        alt: "Waverly Park Kitchen remodel, South Tampa",
      },
      {
        slug: "kitchen-bath-gallery",
        title: "Kitchen & Bath Gallery",
        meta: "Tampa Bay",
        img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_company-site-new/artifacts/2e108beadb37ab18_bcpeabody-tampa-bay-kitchen-remodel-cabinetry-02.jpg",
        alt: "Kitchen remodel with white cabinetry and island seating, Tampa Bay",
        link: "/kitchen-and-bath#gallery",
      },
    ],
  },
  {
    key: "commercial",
    label: "Commercial",
    items: [
      {
        slug: "goddard-school-kennedy-tampa",
        title: "Goddard School",
        meta: "Kennedy Boulevard, Tampa",
        img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/ci6ibh3r_image.png",
        alt: "Goddard School on Kennedy Boulevard, Tampa",
      },
      {
        slug: "hair-cuttery-retail-program",
        title: "Hair Cuttery Build-Out Program",
        meta: "20+ locations, Florida",
        img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/emonanyr_image.webp",
        alt: "Hair Cuttery salon interior, Florida",
      },
    ],
  },
  {
    key: "federal",
    label: "Federal & Civil",
    items: [
      {
        slug: "kissimmee-river-restoration",
        title: "Kissimmee River Restoration, C-38 Reach 3 Backfill",
        meta: "U.S. Army Corps of Engineers, Jacksonville District · Okeechobee County, FL · $4.8M",
      },
      {
        slug: "hernando-beach-navigation-channel",
        title: "Hernando Beach Channel Dredging",
        meta: "Hernando County · Hernando Beach, FL · $8.9M",
      },
      {
        slug: "ir-2-dmma",
        title: "IR-2 Dredged Material Management Area",
        meta: "U.S. Army Corps of Engineers, Jacksonville District · Indian River County, FL · $3.8M",
      },
      {
        slug: "island-creek-berm-repair",
        title: "Island Creek Berm Repair, John H. Kerr Lake",
        meta: "U.S. Army Corps of Engineers, Wilmington District · Boydton, VA · $2.0M",
      },
      {
        slug: "fisher-house-safety-improvements",
        title: "Fisher House Safety Improvements",
        meta: "James A. Haley VA Medical Center · Tampa, FL · $2.0M",
      },
      {
        slug: "tampa-va-front-lobby-renovation",
        title: "VA Front Lobby Renovation",
        meta: "James A. Haley VA Medical Center · Tampa, FL",
      },
      {
        slug: "va-medical-center-renovations",
        title: "VA Medical Center Renovations",
        meta: "Tampa, Gainesville, Lake City and Bay Pines, FL · 25+ task orders including a pharmacy, operating room floors, electrical upgrades and a research lab",
      },
    ],
  },
  {
    key: "consulting",
    label: "Consulting",
    items: [
      {
        slug: "portugues-dam-consulting",
        title: "Portugues Dam, Ponce, Puerto Rico",
        meta: "Federal contracting, quality control, safety and environmental support to the prime contractor on one of the Corps of Engineers' largest dam projects in decades.",
      },
      {
        slug: "c-51-improvements-equitable-adjustment",
        title: "C-51 Improvements, Equitable Adjustment",
        meta: "Palm Beach County, FL · Request for Equitable Adjustment strategy that moved the settlement from a $55K offer to $1.53M.",
      },
      {
        slug: "puerto-rico-fema-pa-recovery",
        title: "Puerto Rico FEMA Public Assistance Recovery",
        meta: "150+ grants for municipalities and private nonprofits.",
        link: "/disaster-recovery",
      },
    ],
  },
];

// Slugs that should not render as clickable cards on the Our Work page.
// These projects don't have detail pages; they're text-only summaries.
const NO_LINK_SLUGS = new Set([
  "ir-2-dmma",
  "island-creek-berm-repair",
  "fisher-house-safety-improvements",
  "va-medical-center-renovations",
]);

const PHOTO_SECTIONS = new Set(["residential", "commercial"]);

function ProjectCard({ item }) {
  return (
    <Link to={item.link || `/projects/${item.slug}`} data-testid={`project-card-${item.slug}`} className="group block hover:opacity-95">
      <div className="relative aspect-[4/3] overflow-hidden bg-ink mb-4">
        <img src={item.img} alt={item.alt || item.title} loading="lazy" className={`w-full h-full object-cover ${item.pos || ""} group-hover:scale-105 transition-transform duration-[1200ms]`} />
      </div>
      <h3 className="font-serif text-2xl text-ink tracking-tight mb-1">{item.title}</h3>
      <p className="text-sm text-ink/60">{item.meta}</p>
    </Link>
  );
}

function ProjectRow({ item }) {
  const noLink = NO_LINK_SLUGS.has(item.slug);
  const body = (
    <>
      <div className="min-w-0">
        <h3 className="font-serif text-xl md:text-2xl text-ink tracking-tight mb-1">{item.title}</h3>
        <p className="text-sm text-ink/65 leading-relaxed">{item.meta}</p>
      </div>
      {!noLink && (
        <span className="shrink-0 inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-copper">
          Read more <ArrowUpRight size={14} />
        </span>
      )}
    </>
  );
  const cls = "group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-8 py-6 border-b border-ink/15";
  if (noLink) return <div data-testid={`project-card-${item.slug}`} className={cls}>{body}</div>;
  return <Link to={item.link || `/projects/${item.slug}`} data-testid={`project-card-${item.slug}`} className={`${cls} hover:opacity-80`}>{body}</Link>;
}

export default function ProjectsIndex() {
  const [filter, setFilter] = useState("all");

  const visibleSections = SECTIONS.filter((s) => filter === "all" || filter === s.key);

  return (
    <>
      <SEO title="Our Work | Residential, Commercial & Federal" description="Custom homes, kitchens and baths, commercial build-outs and federal projects for the Corps of Engineers and the VA, from a Tampa contractor." path="/projects" />
      <Nav />

      <section className="pt-32 pb-10 bg-cream-light">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase mb-5 text-copper">Our Work</div>
          <h1 className="font-serif font-medium text-ink tracking-tight leading-[1.05]" style={{ fontSize: "clamp(34px, 5vw, 60px)" }}>Our Work</h1>
          <p className="mt-6 text-ink/75 max-w-2xl" style={{ fontSize: 17 }}>
            Twenty years of residential, commercial and federal projects.
          </p>

          {/* Filter tabs */}
          <div className="mt-10 flex flex-wrap gap-2 border-b border-ink/10 pb-0" role="tablist" data-testid="projects-filter-tabs">
            {["all", ...SECTIONS.map((s) => s.key)].map((k) => {
              const label = k === "all" ? "All" : SECTIONS.find((s) => s.key === k).label;
              const active = filter === k;
              return (
                <button
                  key={k}
                  data-testid={`projects-filter-${k}`}
                  onClick={() => setFilter(k)}
                  className={`px-4 py-3 text-[11px] tracking-[0.14em] uppercase font-medium border-b-2 -mb-px ${active ? "border-copper text-ink" : "border-transparent text-ink/55 hover:text-ink"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {visibleSections.map((section) => (
        <section key={section.key} data-testid={`projects-section-${section.key}`} className="bg-cream-light pb-16 md:pb-20 pt-10">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-8 text-copper">{section.label}</div>
            {PHOTO_SECTIONS.has(section.key) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {section.items.map((item, i) => (
                  <FadeUp key={item.slug} delay={i * 60}>
                    <ProjectCard item={item} />
                  </FadeUp>
                ))}
              </div>
            ) : (
              <div className="border-t border-ink/15" data-testid={`projects-list-${section.key}`}>
                {section.items.map((item) => <ProjectRow key={item.slug} item={item} />)}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Expert Witness callout */}
      <section data-testid="projects-expert-witness-callout" className="bg-cream py-16 md:py-20 border-t border-ink/10">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] tracking-[0.18em] uppercase text-copper mb-4">Expert Witness</div>
            <h2 className="font-serif font-medium text-ink tracking-tight mb-4" style={{ fontSize: "clamp(24px, 2.8vw, 32px)" }}>
              Expert Witness engagements are confidential.
            </h2>
            <p className="text-ink/75 leading-relaxed max-w-2xl">
              Matters are not published. Contact us to discuss your case under a conflicts check.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/expert-witness" data-testid="projects-ew-link" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-medium text-copper hover:text-copper-deep">
              Learn About Expert Witness Services <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-ink text-cream-light py-20 text-center">
        <div className="max-w-[800px] mx-auto px-6">
          <h2 className="font-serif font-medium mb-8 tracking-tight" style={{ fontSize: "clamp(26px, 3vw, 36px)" }}>Have a similar project?</h2>
          <Link to="/contact" className="btn-copper">Start a Project →</Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
